import { ExtranetContainer } from '@pharmac-extranet/shared'
import { Document, Font, Image, Page, PDFViewer, StyleSheet, Text, View } from '@react-pdf/renderer'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ExtranetViewReturnButton } from '../../components/extranet/ExtranetViewReturnButton'
import { api } from '../../services/HttpService'

Font.register({
  family: 'Barlow',
  fonts: [
    {
      src: '/Barlow-Regular.ttf',
    },
    {
      src: '/Barlow-SemiBold.ttf',
      fontWeight: 600,
    },
  ],
})

const styles = StyleSheet.create({
  body: {
    fontFamily: 'Barlow',
    paddingTop: 30,
    paddingBottom: 65,
    paddingHorizontal: 30,
    fontSize: 12,
  },
  header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    borderBottom: '2px solid #88288f',
    paddingBottom: '16px',
  },
  image: {
    width: 117,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  tableHeadRow: {
    display: 'flex',
    flexDirection: 'row',
    padding: 4,
    backgroundColor: '#f4f4f9',
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
    color: '#88288f',
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 12,
    paddingRight: 30,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'right',
    color: '#282828',
  },
})

export const OrderChangesView = () => {
  const [orderIds, setOrderIds] = useState<number[]>([])
  const { customerId } = useParams()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const orderIds = searchParams.getAll('o').filter((orderId) => !isNaN(+orderId))

    setOrderIds(orderIds.map((a) => Number(a)))
  }, [searchParams])

  const { data: customerData, isLoading: isCustomerLoading } = useQuery({
    queryKey: ['__getCustomer', customerId],
    enabled: !!customerId && !isNaN(+customerId),
    queryFn: async () => await api.customers.getCustomerByCustomerId(Number(customerId as string)),
  })

  const { data, isLoading } = useQuery({
    queryKey: ['__getOrderChangesList', customerId, orderIds],
    queryFn: async () =>
      await api.orders.getOrderChangesList(Number(customerId as string), { orderIds }),
    enabled: !!customerId && !isNaN(+customerId) && orderIds.length > 0,
  })

  if (orderIds.length === 0 || isLoading || isCustomerLoading || !data || !customerData) return null

  const parseDate = (date: string) => {
    if (!/[\d]{4}-[\d]{2}-[\d]{2}/.test(date)) return ''

    const [y, m, d] = date.split('-')

    return `${d}.${m}.${y}`
  }

  return (
    <ExtranetContainer
      full
      paddingLargeHorizontal
      flexColumn
    >
      <ExtranetContainer paddingMediumVertical>
        <ExtranetViewReturnButton />
      </ExtranetContainer>
      <PDFViewer style={{ width: '100%', height: '100vh' }}>
        <Document>
          <Page style={styles.body}>
            <View style={styles.header}>
              <View style={styles.image}>
                <Image src="/pharmac_logo.png" />
              </View>
              <Text style={{ margin: '0 auto', fontWeight: 'semibold' }}>Listaus muutoksista</Text>
              <Text style={{ marginLeft: 'auto', fontWeight: 'semibold' }}>
                {format(new Date(), 'dd.MM.yyyy')}
              </Text>
            </View>
            <View>
              <Text style={{ marginVertical: '18px', fontSize: '16px', fontWeight: 'semibold' }}>
                {customerData.name}
              </Text>
            </View>
            <View>
              {data.groups.map((group) => (
                <View
                  key={group.groupId}
                  style={{ paddingBottom: '18px' }}
                >
                  <Text style={{ padding: '4px 0', fontSize: 12, fontWeight: 'semibold' }}>
                    {group.groupName}
                  </Text>
                  <View>
                    <View style={styles.tableHeadRow}>
                      <Text style={{ width: '40%', fontWeight: 'semibold' }}>Nimi</Text>
                      <Text style={{ width: '18%', fontWeight: 'semibold' }}>Syntymäaika</Text>
                      <Text style={{ width: '27%', fontWeight: 'semibold' }}>Tilausjakso</Text>
                      <Text style={{ width: '15%', fontWeight: 'semibold' }}>Tilausnumero</Text>
                    </View>
                    {group.orders.map((order) => (
                      <>
                        <View
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            padding: 4,
                          }}
                          key={order.id}
                        >
                          <Text style={{ width: '40%', fontWeight: 'semibold' }}>
                            {order.wholeName}
                          </Text>
                          <Text style={{ width: '18%', fontWeight: 'semibold' }}>
                            {order.dateOfBirth}
                          </Text>
                          <Text style={{ width: '27%', fontWeight: 'semibold' }}>
                            {parseDate(order.startDate)}-{parseDate(order.endDate)}
                          </Text>
                          <Text style={{ width: '15%', fontWeight: 'semibold' }}>{order.id}</Text>
                        </View>
                        <View style={{ padding: '0 4' }}>
                          {order.orderChanges.map((change, index) => (
                            <Text
                              style={{ padding: '4 0' }}
                              key={order.id + 'change' + index}
                            >
                              {change}
                            </Text>
                          ))}
                        </View>
                      </>
                    ))}
                  </View>
                </View>
              ))}
            </View>
            <Text
              style={styles.pageNumber}
              render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
              fixed
            />
          </Page>
        </Document>
      </PDFViewer>
    </ExtranetContainer>
  )
}
